import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import type { SourceType, S3SourceConfig, GcpSourceConfig, WhisperModel, OutputFormat } from '../../../request/transcription/transcription.api.ts';

interface SourceSelectorProps {
  onSubmit: (params: {
    sourceType: SourceType;
    sourceConfig: S3SourceConfig | GcpSourceConfig | { gcs_file_ref: string };
    model: WhisperModel;
    language: string;
    formats: OutputFormat[];
    zipFile?: File;
  }) => void;
  loading: boolean;
}

const MODELS: { value: WhisperModel; label: string; note: string }[] = [
  { value: 'tiny',   label: 'Tiny',   note: 'Fastest, lowest accuracy' },
  { value: 'base',   label: 'Base',   note: 'Fast, good accuracy' },
  { value: 'small',  label: 'Small',  note: 'Balanced' },
  { value: 'medium', label: 'Medium', note: 'High accuracy, slower' },
  { value: 'large',  label: 'Large',  note: 'Best accuracy, slowest' },
];

export function SourceSelector({ onSubmit, loading }: SourceSelectorProps) {
  const [sourceType, setSourceType] = useState<SourceType>('zip');
  const [model, setModel] = useState<WhisperModel>('base');
  const [language, setLanguage] = useState('en');
  const [formats, setFormats] = useState<OutputFormat[]>(['docx', 'pdf']);
  const [zipFile, setZipFile] = useState<File | null>(null);

  // S3 fields
  const [s3Bucket, setS3Bucket] = useState('');
  const [s3Prefix, setS3Prefix] = useState('');
  const [s3AccessKey, setS3AccessKey] = useState('');
  const [s3SecretKey, setS3SecretKey] = useState('');
  const [s3Region, setS3Region] = useState('us-east-1');

  // GCP fields
  const [gcpBucket, setGcpBucket] = useState('');
  const [gcpPrefix, setGcpPrefix] = useState('');

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted[0]) setZipFile(accepted[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/zip': ['.zip'] },
    maxFiles: 1,
    disabled: loading,
  });

  const toggleFormat = (fmt: OutputFormat) => {
    setFormats(prev =>
      prev.includes(fmt) ? prev.filter(f => f !== fmt) : [...prev, fmt]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formats.length === 0) return;

    if (sourceType === 'zip') {
      if (!zipFile) return;
      onSubmit({ sourceType, sourceConfig: { gcs_file_ref: '' }, model, language, formats, zipFile });
    } else if (sourceType === 's3') {
      onSubmit({
        sourceType,
        sourceConfig: { bucket: s3Bucket, prefix: s3Prefix, access_key_id: s3AccessKey, secret_access_key: s3SecretKey, region: s3Region },
        model, language, formats,
      });
    } else {
      onSubmit({
        sourceType,
        sourceConfig: { bucket: gcpBucket, prefix: gcpPrefix },
        model, language, formats,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Source type tabs */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Video source</label>
        <div className="flex gap-2">
          {(['zip', 's3', 'gcp'] as SourceType[]).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setSourceType(t)}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                sourceType === t
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {t === 'zip' ? 'Upload ZIP' : t === 's3' ? 'Amazon S3' : 'GCP Bucket'}
            </button>
          ))}
        </div>
      </div>

      {/* ZIP upload */}
      {sourceType === 'zip' && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          {zipFile ? (
            <p className="text-sm text-gray-900 font-medium">{zipFile.name} ({(zipFile.size / 1024 / 1024).toFixed(1)} MB)</p>
          ) : (
            <>
              <p className="text-sm text-gray-600">Drag & drop a ZIP of videos/audio files here</p>
              <p className="text-xs text-gray-400 mt-1">or click to browse</p>
            </>
          )}
        </div>
      )}

      {/* S3 fields */}
      {sourceType === 's3' && (
        <div className="grid grid-cols-2 gap-4">
          <Field label="Bucket" value={s3Bucket} onChange={setS3Bucket} required placeholder="my-bucket" />
          <Field label="Prefix (optional)" value={s3Prefix} onChange={setS3Prefix} placeholder="videos/" />
          <Field label="Access Key ID" value={s3AccessKey} onChange={setS3AccessKey} required />
          <Field label="Secret Access Key" value={s3SecretKey} onChange={setS3SecretKey} required type="password" />
          <Field label="Region" value={s3Region} onChange={setS3Region} placeholder="us-east-1" />
        </div>
      )}

      {/* GCP fields */}
      {sourceType === 'gcp' && (
        <div className="grid grid-cols-2 gap-4">
          <Field label="Bucket" value={gcpBucket} onChange={setGcpBucket} required placeholder="my-gcs-bucket" />
          <Field label="Prefix (optional)" value={gcpPrefix} onChange={setGcpPrefix} placeholder="videos/" />
        </div>
      )}

      {/* Options row */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Whisper model</label>
          <select
            value={model}
            onChange={e => setModel(e.target.value as WhisperModel)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {MODELS.map(m => (
              <option key={m.value} value={m.value}>{m.label} — {m.note}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
          <input
            type="text"
            value={language}
            onChange={e => setLanguage(e.target.value)}
            placeholder="en, es, fr… (blank = auto)"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Output formats</label>
          <div className="flex gap-3 mt-2">
            {(['docx', 'pdf'] as OutputFormat[]).map(fmt => (
              <label key={fmt} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={formats.includes(fmt)}
                  onChange={() => toggleFormat(fmt)}
                  className="rounded"
                />
                {fmt.toUpperCase()}
              </label>
            ))}
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading || formats.length === 0 || (sourceType === 'zip' && !zipFile)}
        className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Starting job…' : 'Start transcription'}
      </button>
    </form>
  );
}

function Field({ label, value, onChange, required, placeholder, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void;
  required?: boolean; placeholder?: string; type?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
}
